import { CustomMeal } from './CustomMeal';

export class Proposal {
  static ProposalState = {
    UNINITIALIZED: 'UNINITIALIZED',
    WAITING: 'WAITING',
    PREPSORT: 'PREPSORT',
    SORTING: 'SORTING',
    DECIDED: 'DECIDED',
    CANCELED: 'CANCELED',
    INVALID: 'INVALID'
  };

  static UserStatus = {
    WAITING: 'WAITING',
    VOTED: 'VOTED',
    DIDNOTVOTE: 'DIDNOTVOTE',
    NOTFINISHED: 'NOTFINISHED',
    NONE: 'NONE'
  };

  constructor(data = {}) {
    this.id = data.id || '';
    this.description = data.description || '';
    this.eventtime = data.eventtime || '';
    this.joined = data.joined || [];
    this.details = data.details || '';
    this.meal = data.meal || '';
    this.state = data.state || Proposal.ProposalState.UNINITIALIZED;
    this.decision = data.decision || '';
    this.location = data.location || null;
    this.range = data.range || null;
    this.cost = typeof data.cost === 'number' ? data.cost : (parseInt(data.cost) || 1);
    this.owner = data.owner || '';
    this.createdtime = data.createdtime || '';
    this.restaurantpool = data.restaurantpool || [];
    this.isPublic = data.public || false;
    this.userSortingData = new Map();

    // Parse user sorting data
    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        if (key.startsWith('sorted-')) {
          const userId = key.substring(7); // Remove "sorted-" prefix
          if (Array.isArray(value)) {
            this.userSortingData.set(userId, value);
          }
        }
      });
    }
  }

  getStateEnum() {
    if (!this.state) {
      return Proposal.ProposalState.UNINITIALIZED;
    }
    try {
      return Proposal.ProposalState[this.state.toUpperCase()];
    } catch (e) {
      return Proposal.ProposalState.UNINITIALIZED;
    }
  }

  setStateEnum(state) {
    this.state = state.toLowerCase();
  }

  getMealName() {
    if (!this.meal) return '';
    const parts = this.meal.split(':');
    return parts.length > 0 ? parts[0].trim() : '';
  }

  getMealCategories() {
    if (!this.meal) return [];
    const parts = this.meal.split(':');
    if (parts.length < 4) return [];
    return parts[3].trim().split(',');
  }

  getFormattedDate() {
    try {
      const date = new Date(this.eventtime);
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    } catch (e) {
      return this.eventtime;
    }
  }

  getCostIndicator() {
    try {
      const costValue = parseInt(this.cost);
      return '$'.repeat(Math.max(0, costValue));
    } catch (e) {
      return '';
    }
  }

  getCostByLocale() {
    try {
      const costValue = parseInt(this.cost);
      const currencySymbol = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(0).replace(/[\d.]/g, '');
      return currencySymbol.repeat(Math.max(0, costValue));
    } catch (e) {
      return '$';
    }
  }

  isOlderThan24Hours() {
    try {
      const eventTime = new Date(this.eventtime);
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return eventTime < twentyFourHoursAgo;
    } catch (e) {
      return false;
    }
  }

  getUserStatus(userId) {
    const state = this.getStateEnum();
    switch (state) {
      case Proposal.ProposalState.UNINITIALIZED:
        return Proposal.UserStatus.WAITING;
      case Proposal.ProposalState.WAITING:
        return Proposal.UserStatus.WAITING;
      case Proposal.ProposalState.PREPSORT:
        return Proposal.UserStatus.WAITING;
      case Proposal.ProposalState.SORTING:
        return this.userSortingData.has(userId) 
          ? Proposal.UserStatus.VOTED 
          : Proposal.UserStatus.NOTFINISHED;
      case Proposal.ProposalState.DECIDED:
        return this.userSortingData.has(userId) 
          ? Proposal.UserStatus.VOTED 
          : Proposal.UserStatus.DIDNOTVOTE;
      case Proposal.ProposalState.CANCELED:
      case Proposal.ProposalState.INVALID:
        return Proposal.UserStatus.NONE;
      default:
        return Proposal.UserStatus.NONE;
    }
  }

  getUserSortedList(userId) {
    return this.userSortingData.get(userId) || [];
  }

  hasUserSortedList(userId) {
    return this.userSortingData.has(userId);
  }

  getCustomMeal() {
    if (!this.meal) return null;
    return new CustomMeal(this.meal);
  }

  toMap() {
    const map = {};
    
    // Add basic fields with null checks
    if (this.id) map.id = this.id;
    if (this.description) map.description = this.description;
    if (this.eventtime) map.eventtime = this.eventtime;
    if (this.details) map.details = this.details;
    if (this.meal) map.meal = this.meal;
    if (this.state) map.state = this.state;
    if (this.decision) map.decision = this.decision;
    if (this.owner) map.owner = this.owner;
    if (this.createdtime) map.createdtime = this.createdtime;
    if (this.cost) map.cost = this.cost;
    if (this.range !== null) map.range = this.range;
    map.public = this.isPublic;

    // Add joined users
    if (this.joined && this.joined.length > 0) {
      map.joined = [...this.joined];
    }

    // Add location
    if (this.location) {
      map.location = {
        latitude: this.location.latitude,
        longitude: this.location.longitude
      };
    }

    // Add restaurant pool
    if (this.restaurantpool && this.restaurantpool.length > 0) {
      map.restaurantpool = [...this.restaurantpool];
    }

    // Add user sorting data
    this.userSortingData.forEach((sortedList, userId) => {
      if (sortedList && sortedList.length > 0) {
        map[`sorted-${userId}`] = [...sortedList];
      }
    });

    return map;
  }

  toJSON() {
    return JSON.stringify(this.toMap());
  }

  generateDescription() {
    // Get meal name using existing method
    let mealName = this.getMealName();
    if (!mealName) {
      mealName = 'Meal';
    }

    // Get proposal date and format it
    let proposalDate;
    try {
      proposalDate = this.eventtime ? new Date(this.eventtime) : new Date();
    } catch (e) {
      proposalDate = new Date();
    }

    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(now);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const lastWeek = new Date(now);
    lastWeek.setDate(lastWeek.getDate() - 7);

    let dateDescription;
    if (proposalDate.toDateString() === now.toDateString()) {
      dateDescription = 'Today';
    } else if (proposalDate.toDateString() === yesterday.toDateString()) {
      dateDescription = 'Yesterday';
    } else if (proposalDate.toDateString() === tomorrow.toDateString()) {
      dateDescription = 'Tomorrow';
    } else if (proposalDate > now && proposalDate <= nextWeek) {
      dateDescription = 'Next week';
    } else if (proposalDate < now && proposalDate >= lastWeek) {
      dateDescription = 'Last week';
    } else {
      // Format as "Month Day, Year"
      dateDescription = proposalDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }

    return `${mealName}, ${dateDescription} near ${this.details}`;
  }
} 